/** @jsxImportSource @emotion/react */
import { PageWrapper } from "../../components/page-skeleton/PageWrapper";
import { CardContainer } from "../../components/page-skeleton/CardContainer";
import { PageHeader } from "../../components/page-skeleton/PageHeader";
import { useGetApiKeys } from "./service/api-keys-api";
import { useSelector } from "react-redux";
import * as authSelectors from "../auth/authSelectors";
import { SpinnerBlock } from "../common/Spinner";
import { CardBody } from "../../components/page-skeleton/CardBody";
import { PlaceholderBox } from "../../components/PlaceholderBox";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { css } from "@emotion/react";
import { Table } from "../../components/table-components/Table";
import { TableRowHeader } from "../../components/table-components/TableRowHeader";
import { TableCellHeaderText } from "../../components/table-components/TableCellHeaderText";
import { TableBody } from "../../components/table-components/TableBody";
import { TableRow } from "../../components/table-components/TableRow";
import { TableCellText } from "../../components/table-components/TableCellText";
import { colors } from "../../components/colors";
import { StyledText } from "../../assets/typography/typography";
import { formatDateWithYearAndTime } from "../../util";
import { PageFooter } from "../../components/page-skeleton/PageFooter";
import { SmallBadge } from "../../components/SmallBadge";
import { useHandleFetchError } from "../../error-handler";

const cardBodyAdditionalStyling = css`
  padding-bottom: 24px;
`;

const badgeAdditionalStyling = css`
  width: fit-content;
`;

export const ApiKeysList = () => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const { data: apiKeys, isLoading, error } = useGetApiKeys(
    organizationKeyName
  );

  useHandleFetchError(error);

  if (isLoading) {
    return <SpinnerBlock />;
  }

  return (
    <PageWrapper>
      <CardContainer>
        <PageHeader title="Api keys" subtitle="Manage all of your api keys" />
        {apiKeys?.length === 0 || !apiKeys ? (
          <CardBody additionalStyles={cardBodyAdditionalStyling}>
            <PlaceholderBox
              icon={<FontAwesomeIcon icon={solid("code")} />}
              header="No api keys created yet"
              subHeader="Contact HeapsGo support to create your first"
            />
          </CardBody>
        ) : (
          <div>
            <Table>
              <thead>
                <TableRowHeader>
                  <TableCellHeaderText>Api key name</TableCellHeaderText>
                  <TableCellHeaderText>Api key</TableCellHeaderText>
                  <TableCellHeaderText>Api key type</TableCellHeaderText>
                  <TableCellHeaderText>Created at</TableCellHeaderText>
                </TableRowHeader>
              </thead>
              <TableBody>
                {apiKeys?.map((apiKey, i) => {
                  return (
                    <TableRow key={i}>
                      <TableCellText className="pl-5">
                        <StyledText
                          color={colors.textPrimary}
                          className="bodyTextRegular mb-0"
                        >
                          {apiKey.name}
                        </StyledText>
                      </TableCellText>
                      <TableCellText>
                        <SmallBadge additionalStyles={badgeAdditionalStyling}>
                          <StyledText
                            color={colors.textPrimary}
                            className="bodyTextRegular mb-0"
                          >
                            {apiKey.apiKey}
                          </StyledText>
                        </SmallBadge>
                      </TableCellText>
                      <TableCellText>
                        <StyledText
                          color={colors.textPrimary}
                          className="bodyTextRegular mb-0"
                        >
                          {apiKey.apiKeyType}
                        </StyledText>
                      </TableCellText>
                      <TableCellText>
                        <StyledText
                          color={colors.textPrimary}
                          className="bodyTextRegular mb-0"
                        >
                          {formatDateWithYearAndTime(apiKey.createdAt)}
                        </StyledText>
                      </TableCellText>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <PageFooter>
              <StyledText className="bodyTextRegular mb-0">
                {apiKeys?.length}{" "}
                {apiKeys?.length === 1 ? "api key" : "api keys"} in total
              </StyledText>
            </PageFooter>
          </div>
        )}
      </CardContainer>
    </PageWrapper>
  );
};
